import { useMemo, memo } from 'react';
import {Fade} from 'react-awesome-reveal';
import clsx from 'clsx';
import {useHistory} from 'react-router-dom';

// Imports => Constants
import {ICONS, THEMES} from '@opp/ui-kit';
import {TITLES, DEFAULT_ROUTE} from '@constants';

// Imports => Utilities
import {
  AcIsSet,
  AcFormatInitials,
  AcSanitize,
} from '@opp/ui-kit';

// Imports => Atoms
import {AcButton, AcIcon} from '@opp/ui-kit';


const _CLASSES = {
  MAIN: 'ac-profile-switcher',
  BUTTON: 'ac-profile-switcher__button',
  AVATAR: 'ac-profile-switcher__avatar',
  INITIALS: 'ac-profile-switcher__initials',
  DETAILS: 'ac-profile-switcher__details',
  NAME: 'ac-profile-switcher__name',
  PARTNER: 'ac-profile-switcher__partner',
  MAIN_ICON: 'ac-profile-switcher__main-icon',
  MAIN_ICON_NO_PROFILES: 'ac-profile-switcher__main-icon-no-profiles',
  ICON: 'ac-profile-switcher__icon',
  INSET_ICON: 'ac-profile-switcher__inset-icon',
};

const AcProfileSwitcher = ({
  hasProfiles, data = null, callback = () => {
  }
}) => {
  const history = useHistory();
  const handleCallback = (event) => {
    if (event && event.persist) {
      event.persist();
    }
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (hasProfiles) {
      if (AcIsSet(callback)) {
        callback(event);
      }
    } else {
      const {replace, location} = history;
      let redirect = DEFAULT_ROUTE.path;

      if (
        AcIsSet(location) &&
                AcIsSet(location.state) &&
                AcIsSet(location.state.from) &&
                AcIsSet(location.state.from.pathname) &&
                location.state.from.pathname !== redirect
      ) {
        redirect = location.state.from;
      }

      replace(redirect);
    }
  };

  const getInitialsClassNames = useMemo(() => {
    return clsx(_CLASSES.INITIALS);
  }, []);

  const getPartnerClassNames = useMemo(() => {
    return clsx(_CLASSES.PARTNER);
  }, []);

  const getNameClassNames = useMemo(() => {
    return clsx(_CLASSES.NAME);
  }, []);

  const getDetailsClassNames = useMemo(() => {
    return clsx(_CLASSES.DETAILS);
  }, []);

  const getMainIconClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN_ICON, !hasProfiles && _CLASSES.MAIN_ICON_NO_PROFILES);
  }, [hasProfiles]);

  const getIconClassNames = useMemo(() => {
    return clsx(_CLASSES.ICON);
  }, []);

  const getInsetIconClassNames = useMemo(() => {
    return clsx(_CLASSES.INSET_ICON);
  }, []);

  const getButtonClassNames = useMemo(() => {
    return clsx(_CLASSES.BUTTON);
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  }, []);

  const getCurrentProfile = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.merchant)) {
      return null;
    }

    return data.merchant;
  }, [data]);

  const getCurrentPartner = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.partner)) {
      return null;
    }

    return data.partner;
  }, [data]);

  const getAvailableProfiles = useMemo(() => {
    if (!AcIsSet(data) || !AcIsSet(data.merchants)) {
      return null;
    }

    return data.merchants;
  }, [data]);

  const getSwitcherButtonOptions = useMemo(() => {
    return {
      id: 'ac-profile-switcher-button',
      theme: THEMES.EGGWHITE,
      callback: handleCallback,
      title: TITLES.SWITCH_MERCHANT,
      disabled: !AcIsSet(getCurrentProfile) || !AcIsSet(getAvailableProfiles),
      className: getButtonClassNames,
    };
  }, [getCurrentProfile, getAvailableProfiles, getButtonClassNames]);

  const renderInitials = useMemo(() => {
    if (!AcIsSet(getCurrentProfile)) {
      return null;
    }

    const {uid, name} = getCurrentProfile;

    return (
      <div
        data-muid={uid}
        className={getInitialsClassNames}
        dangerouslySetInnerHTML={{
          __html: AcFormatInitials(AcSanitize(name)),
        }}
      />
    );
  }, [getCurrentProfile]);

  const renderIcon = useMemo(() => {
    let icon = hasProfiles ? ICONS.USERS_OUTLINE : ICONS.OPP_USER_INDIVIDUAL;

    return (
      <AcIcon className={getMainIconClassNames} icon={icon} />
    );
  }, [hasProfiles]);

  const renderCurrentProfile = useMemo(() => {
    if (!AcIsSet(getCurrentProfile)) {
      return null;
    }

    const {uid, name} = getCurrentProfile;
    const partner = getCurrentPartner;

    return (
      <div className={getDetailsClassNames}>
        <div
          data-muid={uid}
          className={getNameClassNames}
          dangerouslySetInnerHTML={{
            __html: AcSanitize(name),
          }}
        />
        {partner && (
          <div
            className={getPartnerClassNames}
            dangerouslySetInnerHTML={{
              __html: AcSanitize(partner.name),
            }}
          />
        )}
      </div>
    );
  }, [getCurrentProfile, getCurrentPartner]);

  return (
    <div className={getMainClassNames} id={'ac-profile-switcher'}>
      {renderCurrentProfile && (
        <Fade duration={350} key={getCurrentProfile.uid}>
          <AcButton {...getSwitcherButtonOptions}>
            {renderIcon}
            {/*mobile*/}
            {renderInitials}
            {/*desktop*/}
            {renderCurrentProfile}
            {hasProfiles &&
                            <>
                              {/*desktop*/}
                              <AcIcon
                                icon={ICONS.MENU_DOWN}
                                title={TITLES.SWITCH_MERCHANT}
                                className={getIconClassNames}
                              />
                              {/*mobile*/}
                              <AcIcon
                                icon={ICONS.SYNC_ALT}
                                title={TITLES.SWITCH_MERCHANT}
                                className={getInsetIconClassNames}
                              />
                            </>
            }
          </AcButton>
        </Fade>
      )}
    </div>
  );
};

export default memo(AcProfileSwitcher);
